import { REQUEST_STATUS } from "@src/constants";
import { RequestOverviewDetails } from "@src/types";
import {
  AllocationRowData,
  WorkloadTableRowData,
  YearlyAllocations,
} from "@src/types/role_request_types";
import { buildMapFromAllocationList } from "@src/utils/edit_allocation_utils";
import _ from "lodash";

let initialRequestedAllocationMap: Map<string, AllocationRowData> = null;
let childAllocationMap: Map<string, AllocationRowData> = null;

/**
 * Maps the whole request to a data format that the ag grid table can consume.
 *  - **First row** *Requested Workload*: compare requested allocation to child allocation (if a child exists otherwise just take the requested allocation)
 *  - **Second row** *Proposed* (if request has proposed values): compare the requested allocation to the proposed allocation
 * Both rows handle the data in the same way: the allocations have a percentage and an updatedPercentage property.
 * They store the initial value (percentage) and the updated value (updatedPercentage).
 *
 * This is used on team lead side
 *
 * @param {RequestOverviewDetails} request the request that will be mapped to ag grid row data
 * @returns the mapped ag grid row data
 */
export function mapRowData(
  request: RequestOverviewDetails
): WorkloadTableRowData[] {
  const rowData: WorkloadTableRowData[] = [];

  const hasProposedAllocation: boolean = [
    REQUEST_STATUS.PROPOSED,
    REQUEST_STATUS.CONFIRMED,
    REQUEST_STATUS.DECLINED,
  ].includes(request.status);

  // initial requested allocation. will be used for both rows
  initialRequestedAllocationMap = buildMapFromAllocationList(
    request.roleAllocationDetails,
    "requiredPercentage"
  );

  // build first row
  const requestedAllocationRow: WorkloadTableRowData =
    getRequestedAllocationRow(request);

  rowData.push(requestedAllocationRow);

  // build second row if needed
  if (hasProposedAllocation) {
    const proposedAllocationRow: WorkloadTableRowData =
      getProposedAllocationRow(request);

    proposedAllocationRow && rowData.push(proposedAllocationRow);
  }

  return rowData;
}

/**
 * Builds the first row of the table: the requested workload
 *
 * @param {RequestOverviewDetails} request the request that will be mapped (also containing the child)
 * @returns the first row for the workload table
 */
function getRequestedAllocationRow(
  request: RequestOverviewDetails
): WorkloadTableRowData {
  const requestedAllocationMap: Map<string, AllocationRowData> = _.cloneDeep(
    initialRequestedAllocationMap
  );

  if (request.childRequest) {
    childAllocationMap = buildMapFromAllocationList(
      request.childRequest.roleAllocationDetails,
      "requiredPercentage"
    );

    addUpdatedAllocationsToMap(childAllocationMap, requestedAllocationMap);
  }

  return {
    label: "Requested Workload",
    allocations: requestedAllocationMap,
  };
}

/**
 * The requested allocation are compared to the child allocation.
 *
 * - **Scenario 1)** new allocations are added in the child: the child keys are then added to the allocation map -> old value is set to 0, updated value is the child value.
 * - **Scenario 2)** a key of the child map already exists in the allocation map: then the new value will be added at the same key when they differ.
 * - **Scenario 3)** allocations are removed in the child: then the new value is set to 0.
 *
 * @param {Map<string, AllocationRowData>} childAllocationMap the child allocation as a Map
 * @param {Map<string, AllocationRowData>} requestedAllocationMap the Map that will be updated by the child allocation
 */
function addUpdatedAllocationsToMap(
  childAllocationMap: Map<string, AllocationRowData>,
  requestedAllocationMap: Map<string, AllocationRowData>
): void {
  const childKeys: string[] = [...childAllocationMap.keys()];
  const keys: string[] = [...requestedAllocationMap.keys()];

  const addedKeys: string[] = _.difference(childKeys, keys);
  const commonKeys: string[] = _.intersection(childKeys, keys);
  const removedKeys: string[] = _.difference(keys, childKeys);

  for (const key of addedKeys) {
    const newAllocation: AllocationRowData = {
      percentage: 0,
      updatedPercentage: childAllocationMap.get(key).percentage,
    };
    requestedAllocationMap.set(key, newAllocation);
  }

  for (const key of commonKeys) {
    const allocation = requestedAllocationMap.get(key);
    const updatedAllocation = childAllocationMap.get(key);
    if (allocation.percentage !== updatedAllocation.percentage) {
      allocation.updatedPercentage = childAllocationMap.get(key).percentage;
    }
  }

  for (const key of removedKeys) {
    const allocation = requestedAllocationMap.get(key);
    allocation.updatedPercentage = 0;
  }
}

/**
 * Builds the second row of the table: the proposed workload
 *
 * @param {RequestOverviewDetails} request the request that contains the proposed allocation
 * @returns the second row for the workload table or null if no proposed workload exists and the request is in status declined
 */
function getProposedAllocationRow(
  request: RequestOverviewDetails
): WorkloadTableRowData | undefined {
  /* 
  the proposed percentage in roleRequestAllocationDetails are not right.
  they are showing 0 when there is nothing proposed. I cannot difference between a change TO 0 or no change at all.
  since the backend will be updated anyway and any change could have consequences for the current frontend (old design),
  I decided to just take the proposed allocation from proposedYearlyAllocations field 
  */

  /* STEP 1: which allocation need to be considered -> parent or child */
  const { requestedAllocationMap, proposedYearlyAllocations } =
    getAllocationFromChildOrParent(request);

  /* STEP 2: build the propose allocation row data for the second row */
  const proposedAllocationMap: Map<string, AllocationRowData> =
    mapProposedAllocationRowData(
      proposedYearlyAllocations,
      requestedAllocationMap
    );

  /* STEP 3: if the request is declined, compare proposed values to initial values. If they are the same, don't show the second row */
  if (request.status === REQUEST_STATUS.DECLINED) {
    const allProposedValues: AllocationRowData[] = [
      ...proposedAllocationMap.values(),
    ];

    // check if proposed allocation differ from requested allocation (check if there is updatedPercentage somewhere)
    const hasAnyProposedAllocation: boolean = allProposedValues.some(
      (allocation) => allocation.updatedPercentage != null
    );

    if (!hasAnyProposedAllocation) return;
  }

  return {
    label:
      request.status === REQUEST_STATUS.CONFIRMED ? "Confirmed" : "Proposed",
    proposedName: request?.assignedTeamMember,
    allocations: proposedAllocationMap,
  };
}

/**
 * Determines if the child's or parent' s allocations need to be considered.
 * If the request is declined and updated (child exists) then the child's allocations will be returned.
 * Otherwise, the parent's allocation.
 *
 * @param {RequestOverviewDetails} request the request that contains the proposed allocation
 * @returns requested and proposed allocation of either the child or the parent request
 */
function getAllocationFromChildOrParent(request: RequestOverviewDetails): {
  requestedAllocationMap: Map<string, AllocationRowData>;
  proposedYearlyAllocations: YearlyAllocations;
} {
  let requestedAllocationMap: Map<string, AllocationRowData> =
    initialRequestedAllocationMap;
  let proposedYearlyAllocations: YearlyAllocations =
    request.proposedYearlyAllocations;

  // if declined and updated, we need to consider the child allocations
  if (request.status === REQUEST_STATUS.DECLINED && request.childRequest) {
    requestedAllocationMap = childAllocationMap;
    proposedYearlyAllocations = request.childRequest.proposedYearlyAllocations;
  }

  return { requestedAllocationMap, proposedYearlyAllocations };
}

/**
 * The requested allocation are compared to the proposed allocation.
 * Loops through the proposed allocations to add the values of it to a copy of the initial allocation map so that you can see the differences there.
 *
 * - **Scenario 1)** new allocations are added in the proposed allocation: the key is then calculated and added to the allocation map.
 * Old value is set to 0, updated value is the proposed value.
 *
 * - **Scenario 2)** the calculated key already exists in the allocation Map. then it checks id the values are different.
 * If they are different, the value is added. of not, nothing happens.
 *
 * @param {YearlyAllocations} proposedYearlyAllocations the proposed allocation
 * @param {Map<string, AllocationRowData>} requestedAllocationMap the initial requested allocation as a map
 * @returns a Map that contains all proposed allocation including differences if exist
 */
function mapProposedAllocationRowData(
  proposedYearlyAllocations: YearlyAllocations,
  requestedAllocationMap: Map<string, AllocationRowData>
): Map<string, AllocationRowData> {
  if (!proposedYearlyAllocations) return requestedAllocationMap;

  const proposedAllocationMap: Map<string, AllocationRowData> = _.cloneDeep(
    requestedAllocationMap
  );

  Object.entries(proposedYearlyAllocations).forEach(
    ([year, allocationObject]) => {
      Object.entries(allocationObject).forEach(
        ([month, proposedAllocation]) => {
          const key = `${month}-${year}`;
          const requestedAllocation: AllocationRowData =
            proposedAllocationMap.get(key);

          if (!requestedAllocation) {
            const newAllocation: AllocationRowData = {
              percentage: 0,
              updatedPercentage: proposedAllocation,
            };
            proposedAllocationMap.set(key, newAllocation);
          } else if (requestedAllocation.percentage !== proposedAllocation) {
            requestedAllocation.updatedPercentage = proposedAllocation;
          }
        }
      );
    }
  );

  return proposedAllocationMap;
}
